import React, { useState, useEffect } from "react";

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Badge,
  Menu,
  Divider,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";

import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";

import { useNavigate } from "react-router-dom";
import { getNotificationTime } from "../utils/notificationTime";
import Logo from "../assets/Yaazh_logo.png";

import { ApiServices } from "../utils/ApiService";

import {
  useNotification,
} from "../context/NotificationContext";

export default function Header({
  handleDrawerToggle,
}) {
  const navigate = useNavigate();
  const [, setCurrentTime] = useState(Date.now());

  const {
    disconnectSocket,
    notifications,
    count,
    markAsRead,
    markAllAsRead
  } = useNotification();

  const [anchorEl, setAnchorEl] =
    useState(null);

  // ============================================================
  // GET LOGGED-IN USER
  // ============================================================

  const user = React.useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}"
      );
    } catch {
      return {};
    }
  }, []);

  const open = Boolean(anchorEl);

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = async () => {
    try {
      const res =
        await ApiServices.post(
          "user/logout"
        );

      if (res.status === 200) {
        console.log("User Logout");
      }
    } catch (error) {
      console.log(
        "Error while logout:",
        error
      );
    }

    disconnectSocket();

    setAnchorEl(null);

    localStorage.clear();

    navigate("/", {
      replace: true,
    });
  };

  // ============================================================
  // OPEN NOTIFICATION MENU
  // ============================================================

  const handleBellClick = (event) => {
    setAnchorEl(
      event.currentTarget
    );
  };

  // ============================================================
  // CLOSE NOTIFICATION MENU
  // ============================================================

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ============================================================
  // CLICK / VIEW ONE NOTIFICATION
  // ============================================================

  const handleNotificationClick =
    async (notification) => {
      try {
        console.log(
          "Notification clicked:",
          notification
        );

        // ------------------------------------------------------
        // Mark ONLY this notification as read
        // ------------------------------------------------------

        if (!notification.isRead) {
          await markAsRead(
            notification.id
          );
        }

        // ------------------------------------------------------
        // Close notification menu
        // ------------------------------------------------------

        let fileId = notification?.work_file_id?.trim()
        if (!fileId) fileId = notification.message?.match(/file\s+(\d+)/i)?.[1];

        if(fileId) navigate(`/processor-file-view/${fileId}`)
        handleClose();

      } catch (error) {
        console.error(
          "Unable to mark notification as read:",
          error
        );
      }
    };


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // every 1 minute

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#131d38",
        color: "#fff",
        zIndex: (theme) =>
          theme.zIndex.drawer + 1,
      }}
      elevation={2}
    >
      <Toolbar
        sx={{
          position: "relative",
        }}
      >

        {/* =====================================================
            MOBILE MENU BUTTON
        ===================================================== */}

        <IconButton
          edge="start"
          onClick={
            handleDrawerToggle
          }
          sx={{
            mr: 2,
            display: {
              md: "none",
            },
            color: "#fff",
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* =====================================================
            LOGO
        ===================================================== */}

        <Box
          component="img"
          src={Logo}
          alt="Yaazh Logo"
          sx={{
            height: 65,
            width: 65,
            display: {
              xs: "inline-block",
            },
          }}
        />

        {/* =====================================================
            TITLE
        ===================================================== */}

        <Box
          sx={{
            left: 0,
            right: 0,
            pointerEvents:
              "none",
            zIndex: 1,
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{
              pointerEvents:
                "none",
              fontWeight: "bold",
              fontSize: {
                xs: "1.5rem",
                sm: "1.8rem",
                md: "2rem",
              },
              letterSpacing:
                "0.5px",
              color: "#fff",
              textShadow:
                "2px 2px 5px rgba(0, 0, 0, 0.3)",
            }}
          >
            Yaazh Legal Associates
          </Typography>
        </Box>

        {/* =====================================================
            USER DETAILS
        ===================================================== */}

        <Box
          sx={{
            ml: "auto",
            mr: 2,
            display: "flex",
            alignItems:
              "center",
            gap: 1.5,
            backgroundColor:
              "#1e2a4a",
            px: 2,
            py: 0.5,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize:
                "0.95rem",
              color:
                "#26c6da",
            }}
          >
            {user?.name || ""}
          </Typography>

          <Box
            sx={{
              color: "#fff",
              fontSize:
                "0.75rem",
              px: 1.5,
              py: 0.3,
              borderRadius:
                "12px",
              fontWeight: 500,
              textTransform:
                "capitalize",
            }}
          >
            {user?.role || ""}
          </Box>
        </Box>

        {/* =====================================================
            NOTIFICATION BELL
        ===================================================== */}

        <IconButton
          color="inherit"
          onClick={
            handleBellClick
          }
          sx={{
            mr: 1,
            borderRadius: 2,

            "&:hover": {
              bgcolor:
                "rgba(255,255,255,0.08)",
            },
          }}
        >
          <Badge
            badgeContent={count}
            color="error"
            invisible={count === 0}
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: "bold",
              },
            }}
          >
            <NotificationsIcon
              sx={{
                color: "#fff",
              }}
            />
          </Badge>
        </IconButton>

        {/* =====================================================
            NOTIFICATION MENU
        ===================================================== */}

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={
            handleClose
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            elevation: 6,
            sx: {
              width: {
                xs: 330,
                sm: 400,
              },

              maxWidth:
                "calc(100vw - 20px)",

              borderRadius: 2,

              mt: 1,

              overflow: "hidden",
            },
          }}
        >

          {/* ===================================================
              NOTIFICATION HEADER
          =================================================== */}

          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* LEFT SIDE */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
              >
                Notifications
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {count} unread
              </Typography>
            </Box>

            {/* RIGHT SIDE */}
            {count > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  markAllAsRead();
                }}
                sx={{
                  textTransform: "none",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Mark all as read
              </Button>
            )}
          </Box>

          <Divider />

          {/* ===================================================
              SCROLLABLE NOTIFICATION LIST
          =================================================== */}

          <Box
            sx={{
              maxHeight: 450,

              overflowY: "auto",

              "&::-webkit-scrollbar":
                {
                  width: "6px",
                },

              "&::-webkit-scrollbar-thumb":
                {
                  backgroundColor:
                    "#bdbdbd",

                  borderRadius:
                    "10px",
                },

              "&::-webkit-scrollbar-track":
                {
                  backgroundColor:
                    "#f5f5f5",
                },
            }}
          >

            {/* =================================================
                NO NOTIFICATIONS
            ================================================= */}

            {notifications?.length ===
            0 ? (
              <Box
                sx={{
                  p: 4,
                  textAlign:
                    "center",
                }}
              >
                <NotificationsIcon
                  color="disabled"
                  sx={{
                    fontSize: 45,
                    mb: 1,
                  }}
                />

                <Typography
                  color="text.secondary"
                >
                  No notifications
                  available
                </Typography>
              </Box>
            ) : (

              /* =================================================
                 NOTIFICATION LIST
              ================================================= */

              notifications?.map(
                (notification) => (

                  <Box
                    key={
                      notification.id
                    }

                    onClick={() =>
                      handleNotificationClick(
                        notification
                      )
                    }

                    sx={{
                      px: 2,
                      py: 1.5,

                      cursor:
                        "pointer",

                      // ==========================================
                      // UNREAD = BLUE BACKGROUND
                      // READ = WHITE BACKGROUND
                      // ==========================================

                      backgroundColor:
                        notification.isRead
                          ? "#fff"
                          : "#eef6ff",

                      borderLeft:
                        notification.isRead
                          ? "4px solid transparent"
                          : "4px solid #1976d2",

                      transition:
                        "all 0.2s",

                      "&:hover": {
                        backgroundColor:
                          notification.isRead
                            ? "#f5f5f5"
                            : "#e3f2fd",
                      },
                    }}
                  >

                    {/* =========================================
                        TITLE + UNREAD DOT
                    ========================================= */}

                    <Box
                      sx={{
                        display:
                          "flex",

                        alignItems:
                          "center",

                        justifyContent:
                          "space-between",

                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={
                          notification.isRead
                            ? 500
                            : 700
                        }
                        sx={{
                          wordBreak:
                            "break-word",
                        }}
                      >
                        {
                          notification.title
                        }
                      </Typography>

                      {/* =======================================
                          SHOW BLUE DOT ONLY WHEN UNREAD
                      ======================================= */}

                      {!notification.isRead && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            minWidth: 8,

                            borderRadius:
                              "50%",

                            backgroundColor:
                              "#1976d2",
                          }}
                        />
                      )}
                    </Box>

                    {/* =========================================
                        MESSAGE
                    ========================================= */}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {
                        notification.message
                      }
                    </Typography>

                    {/* =========================================
                        TIME
                    ========================================= */}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display:
                          "block",

                        mt: 0.8,
                      }}
                    >
                      {getNotificationTime(notification.created_at)}
                    </Typography>

                  </Box>
                )
              )
            )}

          </Box>
        </Menu>

        {/* =====================================================
            LOGOUT
        ===================================================== */}

        <Button
          onClick={
            handleLogout
          }
          startIcon={
            <LogoutIcon />
          }
          sx={{
            textTransform:
              "none",

            fontWeight: "bold",

            fontSize:
              "1rem",

            color: "#fff",

            borderRadius: 2,

            px: 2,

            transition:
              "all 0.3s ease",

            "&:hover": {
              backgroundColor:
                "#1e2a4a",

              color:
                "#26c6da",
            },

            "& .MuiButton-startIcon":
              {
                color: "#fff",
              },
          }}
          title="Logout"
        >
          <Typography
            sx={{
              display: {
                xs: "none",
                sm: "inline",
              },

              marginLeft: "8px",
            }}
          >
            Logout
          </Typography>
        </Button>

      </Toolbar>
    </AppBar>
  );
}